import React, { useState } from "react";
import { HiTrophy } from "react-icons/hi2";
import { useUserEvents } from "../../../hooks/useUserEvents";
import { linkPerformanceToEvent } from "../../../services/performanceService";
import { formatDateRange } from "../../../utils/goalDates";
import LinkToEventDialog from "./LinkToEventDialog";

interface PerformanceEventLinkProps {
  userId: string;
  trainingLogId: string;
  /** Date of the performance (YYYYMMDD). */
  dateID: string;
  eventId?: string;
  onChanged?: () => void;
}

/** Event name + "Link to event" / "Unlink" for a performance detail view. */
const PerformanceEventLink: React.FC<PerformanceEventLinkProps> = ({
  userId,
  trainingLogId,
  dateID,
  eventId,
  onChanged,
}) => {
  const { events, reload } = useUserEvents(userId);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const event = eventId ? events.find((e) => e.id === eventId) : undefined;

  const handleUnlink = async () => {
    setBusy(true);
    try {
      await linkPerformanceToEvent(userId, trainingLogId, null);
      onChanged?.();
    } catch (error) {
      console.error("Error unlinking performance:", error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 bg-base-100 mx-4 rounded-md shadow-sm flex items-center justify-between gap-3">
      <div className="min-w-0 flex items-center gap-3">
        <HiTrophy className="w-5 h-5 text-base-content/60 flex-shrink-0" />
        <div className="min-w-0">
          <p className="font-light text-xs font-geist">Event</p>
          <p className="text-base font-sans text-base-content truncate">
            {event?.name ?? (eventId ? "Linked event" : "Not linked to an event")}
          </p>
          {event && (
            <p className="text-xs text-base-content/60 font-grotesk">
              {formatDateRange(event.date, event.endDate)}
            </p>
          )}
        </div>
      </div>
      {eventId ? (
        <button
          type="button"
          disabled={busy}
          onClick={handleUnlink}
          className="text-sm text-base-content/70 hover:text-base-content font-grotesk min-h-11 px-2 disabled:opacity-50"
        >
          {busy ? "Unlinking..." : "Unlink"}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="text-sm text-primary font-grotesk font-semibold min-h-11 px-2"
        >
          Link to event
        </button>
      )}
      <LinkToEventDialog
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        userId={userId}
        trainingLogId={trainingLogId}
        dateID={dateID}
        onLinked={() => {
          reload();
          onChanged?.();
        }}
      />
    </div>
  );
};

export default PerformanceEventLink;
